/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import groupStyle from "./groupStyle";
import { Button, FormControl, FormControlTypeMap, FormHelperText, FormLabel, Typography } from "@mui/material";
import React, { ReactNode, useCallback, useEffect } from "react";
import { useForm } from "../../provider/Form/FormProvider";
import { formValue } from "../../services/formServiceFunctions";
import MoreInfoIcon from "../SvgIcon/MoreInfoIcon";
import Pad from "./Pad";
import DialogModal from "../DialogModal/DialogModal";
import _get from "lodash/get";
import { useLocation, useNavigate } from "react-router";
import { ValidatorType } from "../../types/ValidatorType";
import Grid from "@mui/material/Grid";

type GroupOptionProps = {
  labelText: string;
  labelIcon: ReactNode;
  labelIconActive?: ReactNode;
  value: string;
  helperText?: string | ReactNode;
  optionHintText?: string | null;
  headline?: string;
};

export type GroupProps = {
  type: "radio" | "checkbox";
  name: string;
  question: string | ReactNode;
  options: Array<GroupOptionProps>;
  moreInfoText?: string;
  modalHeaderText?: string;
  modalContent?: ReactNode;
  margin?: FormControlTypeMap["props"]["margin"];
  validators?: Array<ValidatorType>;
  dependentValidationFields?: Array<any>;
  direction?: "column" | "row";
};

export default function Group({
  type,
  name,
  question,
  moreInfoText = "Weitere Informationen",
  modalContent,
  modalHeaderText = "Weitere Informationen",
  options,
  margin = "normal",
  validators = [],
  dependentValidationFields = [],
  direction = "column",
}: GroupProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { values, errors, setError, handleChange, registerValidators } = useForm();
  const value = formValue(values, name);
  const isValid = !_get(errors, name);

  useEffect(() => {
    registerValidators(name, validators, dependentValidationFields);
    return () => {
      registerValidators(name, [], []);
      setError(name, undefined);
    };
  }, []);

  const handleChangeInternal = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, value: string | string[]) => {
      const { target } = e;
      if (type === "checkbox" && Array.isArray(value)) {
        if (!value.includes(target.value)) {
          handleChange({
            target: {
              type: "other",
              name: name,
              value: [...value, target.value],
            },
          });
        } else {
          handleChange({
            target: {
              type: "other",
              name: name,
              value: value.filter((entry) => entry !== target.value),
            },
          });
        }
        return;
      }
      handleChange(e);
      setError(name, undefined);
    },
    [type, errors],
  );

  const isActive = useCallback(
    (optionValue: string, value: string | string[]): boolean => {
      if (type === "radio") {
        return value === optionValue;
      }
      if (type === "checkbox") {
        return value.includes(optionValue);
      }
      return false;
    },
    [type],
  );

  return (
    <FormControl component={"fieldset"} margin={margin} error={!isValid} sx={groupStyle.fieldset}>
      <FormLabel component="legend" sx={groupStyle.label}>
        {question}
        {moreInfoText && modalContent && (
          <Button
            fullWidth={true}
            variant={"text"}
            color={"primary"}
            onClick={() => {
              navigate(location.pathname, {
                state: {
                  groupModalOpen: name,
                },
              });
            }}
          >
            <MoreInfoIcon sx={groupStyle.leftIcon} /> {moreInfoText}
          </Button>
        )}
      </FormLabel>
      <Grid container sx={{ width: "100%" }} spacing={{ xs: 0, md: direction === "column" ? 0 : 2 }}>
        {options.map((option) => (
          <Grid size={{ xs: 12, md: direction === "row" ? 6 : 12 }} key={option.value}>
            {option.headline && (
              <Typography variant={"subtitle2"} align={"center"}>
                {option.headline}
              </Typography>
            )}
            <Pad
              fullWidth={direction === "row"}
              {...option}
              onChange={(e) => handleChangeInternal(e, value)}
              type={type}
              name={name}
              active={isActive(option.value, value)}
              error={!isValid}
            />
          </Grid>
        ))}
      </Grid>
      {!isValid && (
        <FormHelperText sx={groupStyle.formHelperText} error={true}>
          {_get(errors, name)}
        </FormHelperText>
      )}
      {modalContent && (
        <DialogModal open={!!(location.state && location.state.groupModalOpen === name)} onClose={() => navigate(-1)} title={modalHeaderText}>
          {modalContent}
        </DialogModal>
      )}
    </FormControl>
  );
}
